import { getZrmUserInfo } from '@zg-rentals/clickstream';
import { ProfileType, PropertyCountRangeType } from '@zg-rentals/ts-rental-manager-api-beta-client';

import { _nextTrackEvent } from '../index';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

const PROFILE_TYPE_TO_CLICKSTREAM: Record<ProfileType, string> = {
  [ProfileType.AGENT]: 'agent_or_broker',
  [ProfileType.LANDLORD]: 'property_owner_or_landlord',
  [ProfileType.PROPERTY_MANAGER]: 'property_manager',
  [ProfileType.REAL_ESTATE_INVESTOR]: 'real_estate_investor',
};

const PROPERTY_COUNT_RANGE_TO_CLICKSTREAM: Record<PropertyCountRangeType, string> = {
  [PropertyCountRangeType.ONE]: '1_unit',
  [PropertyCountRangeType.TWO_TO_TEN]: '2_10_units',
  [PropertyCountRangeType.ELEVEN_TO_FIFTY]: '11_50_units',
  [PropertyCountRangeType.FIFTY_ONE_TO_TWO_HUNDRED]: '51_200_units',
  [PropertyCountRangeType.TWO_HUNDRED_PLUS]: '200_plus_units',
};

const EVENT_IDS = {
  SKIP_ONBOARDING_SETUP: '9531',
  SKIP_NAME_QUESTION: '9532',
  SUBMIT_NAME_QUESTION: '9533',
  SKIP_DESCRIBE_YOU_QUESTION: '9534',
  SUBMIT_DESCRIBE_YOU_QUESTION: '9535',
  SKIP_UNIT_COUNT_QUESTION: '9536',
  SUBMIT_UNIT_COUNT_QUESTION: '9537',
} as const;

export const SKIP_ONBOARDING_SETUP_EVENT_ID = EVENT_IDS.SKIP_ONBOARDING_SETUP;
export const SKIP_NAME_QUESTION_EVENT_ID = EVENT_IDS.SKIP_NAME_QUESTION;
export const SUBMIT_NAME_QUESTION_EVENT_ID = EVENT_IDS.SUBMIT_NAME_QUESTION;
export const SKIP_DESCRIBE_YOU_QUESTION_EVENT_ID = EVENT_IDS.SKIP_DESCRIBE_YOU_QUESTION;
export const SUBMIT_DESCRIBE_YOU_QUESTION_EVENT_ID = EVENT_IDS.SUBMIT_DESCRIBE_YOU_QUESTION;
export const SKIP_UNIT_COUNT_QUESTION_EVENT_ID = EVENT_IDS.SKIP_UNIT_COUNT_QUESTION;
export const SUBMIT_UNIT_COUNT_QUESTION_EVENT_ID = EVENT_IDS.SUBMIT_UNIT_COUNT_QUESTION;

// https://dataportal.zillowgroup.net/event-registry/9531
export const emitSkipOnboardingSetupEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SKIP_ONBOARDING_SETUP,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_name',
      trigger_source_nm: 'skip_setup',
      trigger_object_nm: 'onboarding_header',
    },
    semantic: {
      semantic_event_nm: 'skip_onboarding_setup',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9532
export const emitSkipNameQuestionEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SKIP_NAME_QUESTION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_name',
      trigger_source_nm: 'skip',
      trigger_object_nm: 'onboarding_footer',
    },
    semantic: {
      semantic_event_nm: 'skip_name_question',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9533
export const emitSubmitNameQuestionEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SUBMIT_NAME_QUESTION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_name',
      trigger_source_nm: 'continue',
      trigger_object_nm: 'onboarding_footer',
    },
    semantic: {
      semantic_event_nm: 'submit_name_question',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9534
export const emitSkipDescribeYouQuestionEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SKIP_DESCRIBE_YOU_QUESTION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_describe_you',
      trigger_source_nm: 'skip',
      trigger_object_nm: 'onboarding_footer',
    },
    semantic: {
      semantic_event_nm: 'skip_describe_you_question',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9535
export const emitSubmitDescribeYouQuestionEvent = ({
  user,
  profileType,
}: {
  user: ZrmClickstreamUser | null;
  profileType: ProfileType;
}) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SUBMIT_DESCRIBE_YOU_QUESTION,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_describe_you',
      trigger_source_nm: 'continue',
      trigger_object_nm: PROFILE_TYPE_TO_CLICKSTREAM[profileType],
    },
    semantic: {
      semantic_event_nm: 'submit_describe_you_question',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9536
export const emitSkipUnitCountQuestionEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SKIP_UNIT_COUNT_QUESTION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_unit_count',
      trigger_source_nm: 'skip',
      trigger_object_nm: 'onboarding_footer',
    },
    semantic: {
      semantic_event_nm: 'skip_unit_count_question',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9537
export const emitSubmitUnitCountQuestionEvent = ({
  user,
  unitCountRange,
}: {
  user: ZrmClickstreamUser | null;
  unitCountRange: PropertyCountRangeType;
}) =>
  _nextTrackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SUBMIT_UNIT_COUNT_QUESTION,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'onboarding_unit_count',
      trigger_source_nm: 'continue',
      trigger_object_nm: PROPERTY_COUNT_RANGE_TO_CLICKSTREAM[unitCountRange],
    },
    semantic: {
      semantic_event_nm: 'submit_unit_count_question',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });
